import { useCallback } from "react";
import { toastManager } from "@salestrak/ui";

import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import type { MutationFeedbackOptions } from "#app/lib/hooks/use-team-mutations";
import { AnalyticsContext, MemberEvent, trackEvent } from "#app/lib/analytics";

/**
 * The one write behind every "Reports to" control (profile row, members
 * table, team roster). `null` clears.
 *
 * Picking someone from inside the member's own reporting line is a LIFT: the
 * server moves that person up into the member's old spot and the member
 * slides under them, while everyone else keeps their manager. The mutation
 * result names that person as `liftedMember`, whose new manager folds into
 * the cache with it, and the toast names the move, since a second row
 * changed that nobody pressed.
 *
 * `via` names the control for the `member.set_reports_to` event.
 *
 * `silent`: the caller owns the feedback (a bulk runner announcing one
 * batch), so a failed write REJECTS with no toast of its own instead of
 * resolving null. The lift toast still fires, since it names a second row
 * nobody pressed.
 */
export function useSetReportsTo(via: string) {
  const { userMap } = useWorkspaceData();
  const { updateMember } = useMemberMutations();

  return useCallback(
    async (
      userId: string,
      managerId: string | null,
      options?: MutationFeedbackOptions,
    ) => {
      const result = await updateMember(userId, { managerId }, options);
      if (!result) return null;

      const lifted = "liftedMember" in result ? result.liftedMember : null;
      trackEvent({
        context: AnalyticsContext.Member,
        event: MemberEvent.SetReportsTo,
        data: { via, cleared: managerId == null, lift: lifted != null },
      });
      if (!lifted) return result;

      const previousManager = lifted.managerId ? userMap.get(lifted.managerId) : undefined;
      toastManager.add({
        title: `${lifted.name ?? "Manager"} moved up`,
        description: !lifted.managerId
          ? "Now has no manager, taking this member's old spot."
          : previousManager?.name
            ? `Now reports to ${previousManager.name}, taking this member's old spot.`
            : "Now reports to this member's old manager, taking their spot.",
        type: "success",
      });
      return result;
    },
    [userMap, updateMember, via],
  );
}
